import {ConsentTemplateInputType} from "../../../api/models/consentModels";
import {useTemplateBuilder} from "./context";
import {uniqueInputId} from "./state";
import type {InputBinding, InputKind} from "./types";
import {findInputDefinition} from "./variables";

export const InputInspector = ({ value, onChange, kind }: { value: InputBinding; onChange: (value: InputBinding) => void; kind: InputKind }) => {
  const { inputDefinitions, dispatch } = useTemplateBuilder();
  const definition = findInputDefinition(inputDefinitions, value.actor, value.inputId);
  if (!definition) return <p>This input block has no matching definition.</p>;
  const updateOptions = (options: typeof definition.options) => dispatch({ type: "updateInput", actor: value.actor, inputId: value.inputId, update: { options } });
  const optionId = (label: string, options: NonNullable<typeof definition.options>, currentIndex: number) => {
    const used = new Set(options.filter((_, index) => index !== currentIndex).map((item) => item.id));
    const slug = label.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "option";
    const base = /^[a-z]/.test(slug) ? slug : `option_${slug}`;
    let id = base;
    for (let suffix = 2; used.has(id); suffix += 1) id = `${base}_${suffix}`;
    return id.slice(0, 100);
  };
  const optionType = definition.type === ConsentTemplateInputType.RADIO || definition.type === ConsentTemplateInputType.CHECKBOX_GROUP;
  // Puck custom fields own their immediate form-control lifecycle (including
  // committing labels on blur). Keep native controls at this boundary so Puck
  // can preserve that contract; the consent-scoped stylesheet makes them match
  // the shared staff form controls.
  return <div className="template-builder-input">
    <p>This {kind} input is bound to one reusable definition.</p>
    <label>Actor<select value={value.actor} onChange={(event) => {
      const actor = event.target.value as InputBinding["actor"]; dispatch({ type: "moveInput", input: value, actor });
      const id = uniqueInputId(definition.label, inputDefinitions, value); onChange({ actor, inputId: actor === value.actor ? value.inputId : id });
    }}><option value="employee">Employee</option><option value="family">Family member</option></select></label>
    <label>Label<input defaultValue={definition.label} onBlur={(event) => {
      const label = event.target.value.trim();
      if (!label || label === definition.label) return;
      dispatch({ type: "updateInput", actor: value.actor, inputId: value.inputId, update: { label } });
    }} /></label>
    <code>Input ID: {definition.id}</code>
    {optionType && <section><strong>Choice options</strong>{definition.options?.map((option, index, options) => <div key={`${option.id}-${index}`}><input defaultValue={option.label} onBlur={(event) => {
      const label = event.target.value.trim(); if (!label || label === option.label) return;
      updateOptions(options.map((item, optionIndex) => optionIndex === index ? { ...item, label, id: optionId(label, options, index) } : item));
    }} /><button type="button" onClick={() => updateOptions(options.filter((_, optionIndex) => optionIndex !== index))}>Remove</button></div>)}<button type="button" onClick={() => {
      const options = definition.options ?? [];
      let label = "New option";
      for (let suffix = 2; options.some((option) => option.label.trim().toLocaleLowerCase() === label.toLocaleLowerCase()); suffix += 1) label = `New option ${suffix}`;
      updateOptions(options.concat({ id: optionId(label, options, -1), label }));
    }}>Add option</button></section>}
  </div>;
};
